import { ArrowRight, Zap, Send } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

const packages = [
  {
    id: "review",
    name: "General Estimate Check",
    price: "Quoted per project",
    description: "Review and check your existing estimate",
    features: [
      "Gap analysis",
      "Risk identification",
      "Recommendations",
      "Quick turnaround",
    ],
  },
  {
    id: "materials",
    name: "Materials Takeoff (BOQ)",
    price: "Quoted per project",
    description: "Bill of Quantities with trade breakdown",
    features: [
      "Complete BOQ",
      "Trade-by-trade breakdown",
      "Procurement list",
      "Clear assumptions",
    ],
  },
  {
    id: "labour",
    name: "Labour / Programme Takeoff",
    price: "Quoted per project",
    description: "Labour hours and scheduling breakdown",
    features: [
      "Labour hours by trade",
      "Simple programme",
      "Lead times identified",
      "Constraints documented",
    ],
  },
  {
    id: "full",
    name: "Full Estimate",
    price: "Quoted per project",
    description: "Complete project cost estimation",
    features: [
      "Materials + Labour",
      "Plant & equipment",
      "Subcontract allowances",
      "Margin/OH options",
    ],
    popular: true,
  },
];

export default function Packages({ onGetStarted }) {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate("/#contact");
  };

  return (
    <section
      id="packages"
      className="py-16 md:py-24 border-t-4 border-black bg-zinc-50"
      data-testid="packages-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <div className="mb-12 md:mb-16">
          <span className="font-mono text-sm uppercase tracking-widest text-zinc-500">
            Estimating Services
          </span>
          <h2
            className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl uppercase mt-4"
            data-testid="packages-title"
          >
            Other Services
          </h2>
          <p className="font-body text-zinc-600 mt-4 max-w-xl">
            Beyond Tender Red Review, we offer traditional estimating services. Every project is different - we'll confirm exact scope and fee upfront.
          </p>
        </div>

        {/* Rush turnaround button */}
        <button
          onClick={handleGetStarted}
          className="flex items-center gap-3 mb-8 p-4 border-2 border-black bg-white hover:bg-zinc-100 transition-colors btn-brutal"
          data-testid="rush-turnaround-btn"
        >
          <Zap size={20} strokeWidth={1.5} />
          <span className="font-mono text-sm uppercase tracking-wider">
            Rush turnaround available - get in touch
          </span>
          <ArrowRight size={16} />
        </button>

        {/* Packages grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className="p-6 md:p-8 flex flex-col bg-white border-2 border-black service-card relative"
              data-testid={`package-${pkg.id}`}
            >
              {/* Popular badge */}
              {pkg.popular && (
                <span className="absolute -top-3 left-6 font-mono text-xs uppercase tracking-widest bg-black text-white px-3 py-1">
                  Most Popular
                </span>
              )}

              {/* Name & Price */}
              <div className="mb-6">
                <h3
                  className="font-heading font-bold text-lg md:text-xl uppercase mb-2"
                  data-testid={`package-name-${pkg.id}`}
                >
                  {pkg.name}
                </h3>
                <p
                  className="font-mono text-lg font-medium text-zinc-700"
                  data-testid={`package-price-${pkg.id}`}
                >
                  {pkg.price}
                </p>
                <p className="font-body text-sm mt-2 text-zinc-600">
                  {pkg.description}
                </p>
              </div>

              {/* Features */}
              <ul className="space-y-3 flex-grow mb-6">
                {pkg.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <span className="w-1 h-1 mt-2 flex-shrink-0 bg-black" />
                    {feature}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                onClick={handleGetStarted}
                className="h-12 px-6 uppercase font-bold text-sm tracking-widest border-2 flex items-center justify-center gap-2 bg-black text-white border-black hover:bg-zinc-800 transition-colors"
                data-testid={`package-cta-${pkg.id}`}
              >
                Get Quote
                <ArrowRight size={16} />
              </button>
            </div>
          ))}
        </div>

        {/* Price Desk Promo */}
        <div className="mt-12 p-6 md:p-8 border-2 border-black bg-white">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 border-2 border-black flex items-center justify-center flex-shrink-0">
                <Send size={24} strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-heading font-bold text-xl uppercase mb-2">
                  Price Desk
                </h3>
                <p className="font-body text-zinc-600 max-w-lg">
                  Need supplier quotes managed? Blackline handles RFQs, follow-ups, and quote comparisons so you can focus on the estimate.
                </p>
              </div>
            </div>
            <Link
              to="/price-desk"
              className="h-12 px-6 uppercase font-bold text-sm tracking-widest border-2 flex items-center justify-center gap-2 bg-black text-white border-black hover:bg-zinc-800 transition-colors flex-shrink-0"
              data-testid="price-desk-promo-cta"
            >
              Learn More
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
